import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRequest } from '@axios-use/react';

import { getCouponOrderNoForTiktok } from '@/channels';
import type { CommonResponse } from '@/apis/request';
import type { PromotionCouponProductsType } from '@/apis/product/types';
import type { GetCouponProductListQuery } from '@/apis/product';
import { getCouponProductList, getCouponInfoByDouyinOrder } from '@/apis/product';

export function useGetCouponProductList() {
  const [createCouponProductListReq, CouponProductListReqState] = useRequest(getCouponProductList);
  return {
    createCouponProductListReq,
    CouponProductListReqState,
  };
}

type LocationState = {
  coupon?: {
    couponCode?: string;
    couponId?: string;
    promotionId?: string;
  };
};

export type LocationAutoCouponOptions = {
  params?: Partial<GetCouponProductListQuery>;
  effect?: boolean;
  callback?: (item: PromotionCouponProductsType | undefined, coupon: Required<LocationState>['coupon']) => unknown;
  onError?: (msg: string) => void;
};

export function useLocationAutoCoupon(options?: LocationAutoCouponOptions) {
  const { params, effect, callback, onError } = options || {};
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState;

  const { createCouponProductListReq } = useGetCouponProductList();
  const [craeteCouponInfoByDouyinOrderReq] = useRequest(getCouponInfoByDouyinOrder);
  const checkCoupon = useCallback(async () => {
    let coupon = state?.coupon;
    if (!coupon) {
      const _order_no = getCouponOrderNoForTiktok();
      if (_order_no) {
        const [info] = await craeteCouponInfoByDouyinOrderReq({ outRequestNo: _order_no }).ready();
        coupon = info;
      }
    }

    if (coupon && coupon.couponCode && coupon.couponId && coupon.promotionId) {
      if (params && params.daypartCode && params.storeCode && params.orderType) {
        try {
          const [data, response] = await createCouponProductListReq({
            ...coupon,
            ...params,
          } as GetCouponProductListQuery).ready();

          const _coupon = { ...coupon };
          // clear state
          navigate(location, { replace: true, state: undefined });

          const _res = response as unknown as CommonResponse;
          if (_res?.success === true && data) {
            // ignore productList empty
            callback?.(data, _coupon);
          } else if (_res?.success === false) {
            const _rMsg = _res.message;
            const _errMsg = typeof _rMsg === 'string' ? _rMsg : '获取券信息失败';
            onError?.(_errMsg);
          }
        } catch (error) {
          console.error('[useLocationAutoCoupon] fetch err', error);
          navigate(location, { replace: true, state: undefined });
        }
      }
    }
  }, [
    callback,
    craeteCouponInfoByDouyinOrderReq,
    createCouponProductListReq,
    location,
    navigate,
    params,
    state?.coupon,
  ]);

  const checkCouponRef = useRef(checkCoupon);
  useEffect(() => {
    checkCouponRef.current = checkCoupon;
  }, [checkCoupon]);

  useEffect(() => {
    if (effect) {
      void checkCouponRef.current();
    }
  }, [effect]);

  return checkCouponRef.current;
}
